import type { ReactNode } from 'react'
import { DateField, TextField } from '@shared/design-system/ui'
import { formatDocument } from '@shared/lib/format/masks'
import { onlyDigits } from '@shared/lib/validation/documents'
import { accountSection, accountSectionTitle, full, grid, sectionCopy } from './onboarding.css'

export type RepresentativeFieldsValue = {
  firstName: string
  lastName: string
  birthDate: string
  documentNumber: string
  title: string
}

export type RepresentativeFieldErrors = Partial<Record<keyof RepresentativeFieldsValue, string>>

export function ProfileRepresentativeFields({
  value,
  errors,
  hints,
  disabled = false,
  onChange,
  onBlur,
  children,
}: {
  value: RepresentativeFieldsValue
  errors: RepresentativeFieldErrors
  hints?: RepresentativeFieldErrors
  disabled?: boolean
  onChange: (value: RepresentativeFieldsValue) => void
  onBlur?: (field: keyof RepresentativeFieldsValue) => void
  /** The representative's contact fields, kept in the profile form state. */
  children?: ReactNode
}) {
  return (
    <section className={`${full} ${accountSection}`}>
      <h3 className={accountSectionTitle}>Representante legal</h3>
      <p className={sectionCopy}>
        Quem preenche este cadastro deve ser o representante legal da empresa. Nome, nascimento e
        telefone informados neste perfil devem ser dessa pessoa.
      </p>
      <div className={grid}>
        <TextField
          label="Nome do representante"
          value={value.firstName}
          disabled={disabled}
          error={errors.firstName}
          data-profile-check-hint={Boolean(hints?.firstName) || undefined}
          helperText={hints?.firstName}
          onBlur={() => onBlur?.('firstName')}
          onChange={(event) => onChange({ ...value, firstName: event.target.value })}
        />
        <TextField
          label="Sobrenome do representante"
          value={value.lastName}
          disabled={disabled}
          error={errors.lastName}
          data-profile-check-hint={Boolean(hints?.lastName) || undefined}
          helperText={hints?.lastName}
          onBlur={() => onBlur?.('lastName')}
          onChange={(event) => onChange({ ...value, lastName: event.target.value })}
        />
        <DateField
          label="Data de nascimento do representante"
          value={value.birthDate}
          disabled={disabled}
          error={errors.birthDate}
          data-profile-check-hint={Boolean(hints?.birthDate) || undefined}
          helperText={hints?.birthDate}
          onBlur={() => onBlur?.('birthDate')}
          onChange={(event) => onChange({ ...value, birthDate: event.target.value })}
        />
        <TextField
          label="CPF do representante legal"
          placeholder="000.000.000-00"
          value={formatDocument(value.documentNumber)}
          disabled={disabled}
          error={errors.documentNumber}
          data-profile-check-hint={Boolean(hints?.documentNumber) || undefined}
          helperText={hints?.documentNumber}
          onBlur={() => onBlur?.('documentNumber')}
          onChange={(event) =>
            onChange({ ...value, documentNumber: onlyDigits(event.target.value).slice(0, 11) })
          }
        />
        <TextField
          className={full}
          label="Cargo do representante legal"
          maxLength={100}
          value={value.title}
          disabled={disabled}
          error={errors.title}
          data-profile-check-hint={Boolean(hints?.title) || undefined}
          helperText={hints?.title}
          onBlur={() => onBlur?.('title')}
          onChange={(event) => onChange({ ...value, title: event.target.value })}
        />
        {children}
      </div>
    </section>
  )
}
